/**
 * Product Navigation Module
 * Handles instant navigation with preloading, keyboard controls, and swipe gestures
 */
class ProductNavigation {
  constructor() {
    this.navData = null;
    this.isNavigating = false;
    this.preloadedData = {};
    this.touchStartX = null;
    this.touchStartY = null;
    this.minSwipeDistance = 50;

    this.init();
  }

  init() {
    // Detect Shopify theme editor and add class to body
    if (window.Shopify && window.Shopify.designMode) {
      document.body.classList.add('shopify-design-mode');
    }

    // Initialize sticky header detection
    this.initStickyHeader();

    // Get navigation data from the page
    const navDataElement = document.getElementById('product-nav-data');
    if (!navDataElement) return;

    try {
      this.navData = JSON.parse(navDataElement.textContent);
    } catch (e) {
      console.error('Failed to parse navigation data:', e);
      return;
    }

    // Initialize features
    this.initKeyboardNavigation();
    this.initSwipeGestures();
    this.initRecentlyViewed();
    this.preloadAdjacentProducts();
    this.enhanceNavigationLinks();
    this.initVisibilityToggle();

    // Track current product view
    this.trackProductView();
  }

  /**
   * Initialize sticky header detection
   */
  initStickyHeader() {
    const stickyHeader = document.querySelector('.product-nav-fixed');
    if (!stickyHeader) return;

    // Use Intersection Observer to detect when element becomes sticky
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < 1) {
          stickyHeader.classList.add('is-stuck');
        } else {
          stickyHeader.classList.remove('is-stuck');
        }
      },
      { threshold: [1] }
    );

    observer.observe(stickyHeader);
  }

  /**
   * Keyboard navigation (arrow keys)
   */
  initKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      // Don't navigate if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Don't navigate if modifier keys are pressed
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

      if (e.key === 'ArrowLeft' && this.navData.navigation.previous.url) {
        e.preventDefault();
        this.navigateToProduct(this.navData.navigation.previous.url, 'keyboard');
      } else if (e.key === 'ArrowRight' && this.navData.navigation.next.url) {
        e.preventDefault();
        this.navigateToProduct(this.navData.navigation.next.url, 'keyboard');
      }
    });
  }

  /**
   * Touch swipe gestures for mobile
   */
  initSwipeGestures() {
    if (!('ontouchstart' in window)) return;

    const productContainer = document.querySelector('.product-main, main');
    if (!productContainer) return;

    productContainer.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    }, { passive: true });

    productContainer.addEventListener('touchend', (e) => {
      if (!this.touchStartX || !this.touchStartY) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;

      // Check if horizontal swipe is more significant than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.minSwipeDistance) {
        if (deltaX > 0 && this.navData.navigation.previous.url) {
          // Swipe right - go to previous product
          this.navigateToProduct(this.navData.navigation.previous.url, 'swipe');
        } else if (deltaX < 0 && this.navData.navigation.next.url) {
          // Swipe left - go to next product
          this.navigateToProduct(this.navData.navigation.next.url, 'swipe');
        }
      }

      this.touchStartX = null;
      this.touchStartY = null;
    }, { passive: true });
  }

  /**
   * Recently viewed products tracking and fallback
   */
  initRecentlyViewed() {
    // If no navigation found via collections, try recently viewed
    if (!this.navData.shouldShow) {
      this.loadRecentlyViewedNavigation();
    }
  }

  trackProductView() {
    const maxRecentProducts = 20;
    let recentProducts = this.getRecentProducts();

    const currentProduct = {
      id: this.navData.currentProduct.id,
      handle: this.navData.currentProduct.handle,
      title: this.navData.currentProduct.title,
      url: window.location.pathname,
      timestamp: Date.now()
    };

    // Remove if already exists and add to beginning
    recentProducts = recentProducts.filter(p => p.id !== currentProduct.id);
    recentProducts.unshift(currentProduct);

    // Keep only the most recent products
    recentProducts = recentProducts.slice(0, maxRecentProducts);

    localStorage.setItem('recently_viewed_products', JSON.stringify(recentProducts));
  }

  getRecentProducts() {
    try {
      return JSON.parse(localStorage.getItem('recently_viewed_products') || '[]');
    } catch {
      return [];
    }
  }

  loadRecentlyViewedNavigation() {
    const recentProducts = this.getRecentProducts();
    const currentIndex = recentProducts.findIndex(p => p.id === this.navData.currentProduct.id);

    if (currentIndex > 0) {
      // Has a previous product in recently viewed
      const prevProduct = recentProducts[currentIndex - 1];
      this.addRecentlyViewedButton('prev', prevProduct);
    }

    if (currentIndex >= 0 && currentIndex < recentProducts.length - 1) {
      // Has a next product in recently viewed
      const nextProduct = recentProducts[currentIndex + 1];
      this.addRecentlyViewedButton('next', nextProduct);
    }
  }

  addRecentlyViewedButton(direction, product) {
    const selector = direction === 'prev'
      ? '.product-nav-top__link--prev[data-nav-prev], .product-nav-mobile__button--prev'
      : '.product-nav-top__link--next[data-nav-next], .product-nav-mobile__button--next';

    const existingButtons = document.querySelectorAll(selector);

    // Only add if buttons don't already exist
    if (existingButtons.length === 0) {
      // Create navigation buttons dynamically
      this.createNavigationButton(direction, product);
    }
  }

  createNavigationButton(direction, product) {
    // This would create the button HTML dynamically
    // Implementation depends on your exact HTML structure
    console.log(`Would create ${direction} button for ${product.title}`);
  }

  /**
   * Preload adjacent product data for instant navigation
   */
  async preloadAdjacentProducts() {
    const urls = [];

    if (this.navData.navigation.previous.url) {
      urls.push(this.navData.navigation.previous.url);
    }

    if (this.navData.navigation.next.url) {
      urls.push(this.navData.navigation.next.url);
    }

    // Preload product JSON data
    urls.forEach(url => {
      this.preloadProductData(url);
    });

    // Preload full pages using link prefetch (already done in Liquid)
    // Additional JavaScript prefetching for browsers that don't support link prefetch
    if (!('link' in document.createElement('link'))) {
      urls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      });
    }
  }

  async preloadProductData(url) {
    // Extract product handle from URL
    const handle = url.split('/').pop().split('?')[0];
    const jsonUrl = `/products/${handle}.js`;

    try {
      const response = await fetch(jsonUrl);
      const data = await response.json();
      this.preloadedData[handle] = data;
    } catch (e) {
      console.log('Failed to preload product data:', e);
    }
  }

  /**
   * Enhance navigation links with smooth transitions
   */
  enhanceNavigationLinks() {
    const navLinks = document.querySelectorAll('[data-nav-prev], [data-nav-next], [data-nav-prev-breadcrumb], [data-nav-next-breadcrumb]');

    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        // Allow default navigation but track the event
        const direction = link.hasAttribute('data-nav-prev') || link.hasAttribute('data-nav-prev-breadcrumb') ? 'previous' : 'next';
        this.trackNavigation(direction, 'click');
      });
    });
  }

  /**
   * Navigate to product with optional transition
   */
  navigateToProduct(url, source) {
    if (this.isNavigating) return;
    this.isNavigating = true;

    // Track navigation event
    this.trackNavigation(url.includes(this.navData.navigation.previous.url) ? 'previous' : 'next', source);

    // For now, do a regular navigation
    // In the future, this could be enhanced with AJAX loading
    window.location.href = url;
  }

  /**
   * Track navigation analytics
   */
  trackNavigation(direction, source) {
    // Track with Google Analytics if available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'product_navigation', {
        'event_category': 'engagement',
        'event_label': direction,
        'navigation_source': source,
        'navigation_method': this.navData.navigation.method,
        'collection': this.navData.navigation.collection.handle
      });
    }

    // Track with Shopify analytics if available
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.track('Product Navigation', {
        direction: direction,
        source: source,
        method: this.navData.navigation.method,
        collection: this.navData.navigation.collection.handle
      });
    }
  }

  /**
   * Show/hide navigation based on scroll position
   */
  initVisibilityToggle() {
    const fixedNav = document.querySelector('[data-product-nav-fixed]');
    if (!fixedNav) return;

    const variant = fixedNav.dataset.navVariant || 'floating';
    if (variant === 'top') {
      fixedNav.classList.add('product-nav-fixed--visible');
      return;
    }

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateVisibility = () => {
      const currentScrollY = window.scrollY;

      // Show navigation when scrolled down past product images
      if (currentScrollY > 500) {
        fixedNav.classList.add('product-nav-fixed--visible');
      } else {
        fixedNav.classList.remove('product-nav-fixed--visible');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateVisibility);
        ticking = true;
      }
    }, { passive: true });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductNavigation();
  });
} else {
  new ProductNavigation();
}

// Export for use in other modules
window.ProductNavigation = ProductNavigation;
